import React, {useCallback, useContext, useEffect, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import * as Sentry from "@sentry/react";
import {Button} from "../../../components/common/Button";
import dayjs from "dayjs";
import {
    ButtonsDiv,
    Title,
    CurrencyBalance,
    AddFundsHeader,
    Icon,
    Modal,
    ErrorWrapper,
    SendPaymentWrapper,
    Heading,
} from "./style";
import CurrencySelector from "../components/CurrencySelector/CurrencySelector";
import ErrorMessage from "../../../components/ErrorMessage";
import CustomModal from "../../../components/common/CustomModal";
import CurrencyFlag from "../components/CurrencyFlag";
import ReceiveFunds from "../components/ReceiveFunds";
import SendFunds from "../components/SendFunds/index";
import {useAppDispatch, useAppSelector} from "../../../app/hooks";
import {
    CURRENCIES_ALLOWED_FOR_VA,
    CURRENCIES_FOR_PAYOUT_V2,
    CURRENCY_CODE_WITHOUT_CLASS,
    EXCLUDED_CURRENCIES,
    EXCLUDED_CURRENCIES_FOR_VA, EXCLUDED_CURRENCY_FOR_SEND,
    formatBool,
} from "../utils";
import {FEATURE_FLAG_KEYS} from "../../../utils/featureFlags";
import { formatCurrencyCodeAmount } from "../../../utils/currency";

import ReceiveWithNonNGN from "../components/ReceiveFunds/ReceiveWithNonNGN";
import TransactionStatus from "../../../components/transactions/components/TransctionStatus";
import CustomTable from "../../../components/common/CustomTable";
import {
    EmptyWrapper,
    EmptyWrapperSubTitle,
    EmptyWrapperTitle,
} from "../../Balances/style";
import {addFunds, updateRequest} from "../api";
import Loader from "../../../components/common/Loader";
import KlashaAccount from "../components/ReceiveFunds/KlashaAccount";
import {formatLongString} from "../../../utils/formatString";
import Table from "../components/Table/Table";
import Menu from "../components/Menu/Menu";
import {NetworkErrorAlertContext} from "../../../context/NetworkErrorAlert";
import {SuccessAlertContext} from "../../../context/SuccessAlertContext";
import {selectUserRoleAndPermissions} from "../../Settings/Team/redux/slice";
import {
    allVirtualAccounts,
    allWallets,
    createVirtualAccountV3,
    GetVirtualAccountStatus,
} from "../redux/slice";
import BASSAccount from "../components/BASSAccount/BASSAccount";
import {FundingContent} from "../components/FundingContent";
import {encryptAlgorithm} from "../../../utils/encryptionAlgorithm";
import {GHS_SEND_ALLOW_LIST, MINIMUM_USD} from "../../../utils/const";
import {stableCoinsArray, WALLET_MENU_ITEMS} from "../const";
import {WalletState} from "../../../types/walletTypes";
import {capitalizeCurrencyCode} from "../../../utils/helpers";
import {
    getWalletDepositRequest,
} from "../../../app/queries/wallets";
import {useQueryClient} from "react-query";
import {useFeatureFlagEnabled} from "posthog-js/react";
import ReceiveStableCoin from "../components/StableCoins/ReceiveStableCoin";
import posthog from "posthog-js";
import { updateFormData } from "../redux/formDataSlice";

const EmptyBalanceDescription = () => {
    return (
        <EmptyWrapper>
            <EmptyWrapperTitle>No transactions yet</EmptyWrapperTitle>
            <EmptyWrapperSubTitle>
                All received or sent transactions will appear here
            </EmptyWrapperSubTitle>
        </EmptyWrapper>
    );
};

const Wallet = () => {
    const {currencyCode} = useParams();

    const {wallets, virtualAccounts, loading} = useAppSelector(
        (state) => state?.wallets,
    );
    const walletVirtualAccounts = Array.isArray(virtualAccounts)
        ? virtualAccounts
        : [];
    const findVirtualAccount = walletVirtualAccounts.find(
        (data) => data.currency === capitalizeCurrencyCode(currencyCode),
    );

    const CURRENT_WALLET = wallets?.find(
        (wallet) => wallet.currency === capitalizeCurrencyCode(currencyCode),
    );

    const successAlertContext = useContext(SuccessAlertContext);


    const queryClient = useQueryClient();

    const navigate = useNavigate();
    const {onShowAlert: onSuccess} = successAlertContext || {};
    const {onShowAlert} = useContext(NetworkErrorAlertContext);
    const {businessId} = useAppSelector((state) => state.users);
    const {business} = useAppSelector((state) => state.settings);
    const [minimumRecipientAmount] = useState(MINIMUM_USD);
    const [walletState, setWalletState] = useState<WalletState>({} as WalletState);

    const hideGhsSendButton = useFeatureFlagEnabled(FEATURE_FLAG_KEYS.hideGhsSendButton);

    const { email } = useAppSelector((state) => state.users.userObj.accountUser);
    const enableStableCoins = useFeatureFlagEnabled(FEATURE_FLAG_KEYS.enableStableCoins);
    
    useEffect(() => {
        if (email) {
            posthog.identify(email, { email });
        }
    }, [email]);

    const findWalletId = wallets?.find(
        (wallets) => wallets?.currency === capitalizeCurrencyCode(currencyCode),
    );
    const {id: walletID} = findWalletId ?? {id: null};

    const {role, permissions} = useAppSelector(selectUserRoleAndPermissions);
    const dispatch = useAppDispatch();

    const {onShowAlert: onShowErrorAlert} = useContext(
        NetworkErrorAlertContext,
    );

    const depositRequestPayload = {
        walletId: walletID,
        pageNumber: walletState?.pageNumber,
        pageSize: 5,
    };
    const {data: depositRequestData, isLoading: allDepositRequestLoading} =
        getWalletDepositRequest(depositRequestPayload);

    const DEPOSIT_REQUEST = depositRequestData?.fundingBusinessWalletRequests.map(
        (item) => ({
            key: item.id,
            ref: formatLongString(item.reference, 10),
            amount: formatCurrencyCodeAmount(item.currency, item.fundAmount),
            narration: item.reference,
            date: dayjs(item.createdAt).format("DD.MM.YYYY - HH:mm"),
            payment_status: formatBool(item.paid),
            credit_status: item.creditStatus,

            narrationAmount: item.fundAmount,
        }),
    );


    const hasPayoutPermission = permissions?.["wallet"]?.includes(
        "CREATE_WALLET_PAYOUT",
    );
    const hasTopUpPermission =
        permissions?.["wallet"]?.includes("CREATE_ACCOUNT_VA");

    const DEPOSIT_REQUEST_COLUMNS = [
        {
            title: "Reference",
            dataIndex: "ref",
            key: "ref",
        },
        {
            title: "Amount",
            dataIndex: "amount",
            key: "amount",
        },
        {
            title: "Date",
            dataIndex: "date",
            key: "date",
        },
        {
            title: "Payment status",
            dataIndex: "payment_status",
            key: "payment_status",
            render: (text: string) => <TransactionStatus text={text}/>,
        },

        {
            title: "Credit status",
            dataIndex: "credit_status",
            key: "credit_status",
            render: (text: string) => <TransactionStatus text={text}/>,
        },
        {
            title: "",
            dataIndex: "operation",
            key: "operation",
            render: () => (
                <Menu
                    items={WALLET_MENU_ITEMS}
                    handleItemClick={handleItemClick}
                    tableData={walletState?.tableData}
                />
            ),
        },
    ];

    const handleItemClick = async (key) => {
        if (key === "1") {
            handleDataUpdate("requestModal", false);
            handleDataUpdate("updatePayment", true);
        } else if (key === "2") {
            await onCopyLinkToClipboard(walletState.tableRef?.narration);
        }
    };

    const handleRequestVirtualAccount = useCallback(async () => {
        const requestData = {
            currency: capitalizeCurrencyCode(currencyCode),
            businessId: businessId,
        };

        const action = await dispatch(createVirtualAccountV3(requestData));
        if (createVirtualAccountV3.fulfilled.match(action)) {
            await dispatch(allWallets({businessId}));
            await dispatch(allVirtualAccounts({ businessId }));
            onSuccess("Virtual account created successfully.");
            handleDataUpdate("accountModal", false);
            handleDataUpdate("showReceiveModal", true);
            return true;
        }

        if (createVirtualAccountV3.rejected.match(action)) {
            onShowAlert("", action.payload as string);
        }

        return false;
    }, [businessId, currencyCode, dispatch, onShowAlert, onSuccess]);

    const onCopyLinkToClipboard = async (data) => {
        await navigator.clipboard.writeText(data);
    };

    const handleDataUpdate = (field, value) => {
        setWalletState((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const getBassVirtualAccountStatus = async () => {
        handleDataUpdate("loading", true);

        try {
            const data = await dispatch(GetVirtualAccountStatus({businessId}));

            if (GetVirtualAccountStatus.fulfilled.match(data)) {
                const {status} = data.payload.data;
                handleStatusUpdate(status);
            }

            if (GetVirtualAccountStatus.rejected.match(data)) {
                handleDataUpdate("failed", true);
            }
        } catch (error) {
            Sentry.captureException(error);
        } finally {
            handleDataUpdate("loading", false);
        }
    };

    const handleStatusUpdate = (status) => {
        const statusMap = {
            PROCESSED: "completed",
            NOTFOUND: "failed",
            PENDING: "pending",
        };

        const newStatus = statusMap[status];
        handleDataUpdate("status", newStatus);
    };

    useEffect(() => {
        if (businessId && currencyCode === "usd") {
            getBassVirtualAccountStatus().then(r => r);
        }
    }, []);

    const onPageChange = (page: number) => {
        handleDataUpdate("pageNumber", page);
    };

    const handleReceiveFunds = async () => {
        try {
            handleDataUpdate("loading", true);

            const data = {
                email: business?.email,
                businessName: business?.name,
                currency: capitalizeCurrencyCode(currencyCode),
                fundAmount: walletState.amount,
                walletId: walletID,
                businessId: businessId,
            };

            const encryptedData = encryptAlgorithm(data);
            const payload = {message: encryptedData};

            await addFunds(payload).then((data) => {
                handleDataUpdate("tableRef", {
                    ...walletState.tableRef,
                    narration: data?.data?.reference
                });
                handleDataUpdate('topUpRequestId', data?.data?.id)
            });

        } catch (error) {
            onShowAlert("", error?.response?.data.message);

        } finally {
            handleDataUpdate("loading", false);
            handleDataUpdate("showKlashaAccount", true)
            handleDataUpdate("amount", '')
        }
    };


    const handleOpenRequests = () => {
        handleDataUpdate("requestModal", true);
    };

    const handleCloseModal = () => {
        handleDataUpdate("showReceiveModal", false);
        handleDataUpdate("showKlashaAccount", false);
    };

    const handleUpdatePayment = async () => {
        handleDataUpdate("loading", true);

        try {
            if (currencyCode === "zar") {
                await updateRequest(walletState?.topUpRequestId);
            } else {
                await updateRequest(walletState?.tableRef?.narration);
            }

            handleDataUpdate("showReceiveModal", false);
            handleDataUpdate("showKlashaAccount", false);

            await queryClient.invalidateQueries(["walletDepositRequest"]);

            onSuccess("Request successfully submitted");

        } catch (error) {
            onShowAlert("", error?.response?.data?.message);

        } finally {
            handleDataUpdate("loading", false);
        }
    };


    const handleRowClick = (row) => {
        setWalletState((prev) => ({
            ...prev,
            tableRef: row,
        }));
        handleDataUpdate('topUpRequestId', row.key)
    };

    const handleTopUp = () => {
        if (hasTopUpPermission || role === "OWNER") {
            if (businessId && business?.approved) {
                handleDataUpdate("showReceiveModal", true);
            } else {
                onShowErrorAlert("", "Business awaiting approval.");
            }
        } else {
            onShowErrorAlert("", "Access is denied");
        }
    };


    const handleSend = () => {
        if (currencyCode === "usd") {
            navigate("/wallets/withdrawal/bass");
        } else if (currencyCode === "cny") {
            navigate(`/wallets/${currencyCode}/payout`);
        } else if (CURRENCIES_FOR_PAYOUT_V2.includes(currencyCode)) {
            if (currencyCode === "ngn") {
                dispatch(updateFormData({ step: 0, payoutv2Data: {} }));
            }
            navigate(`/wallets/${currencyCode}/v2`)
        } else if (!hasPayoutPermission && role === "OWNER") {
            onShowErrorAlert("", "Access is denied");
        } else if (!businessId || !business?.approved) {
            onShowErrorAlert("", "Business awaiting approval");
        } else {
            handleDataUpdate("showSendModal", true);
        }
    };


    const handleBankTransfer = useCallback(async () => {
        let existingAccount = findVirtualAccount;

        if (!existingAccount && businessId) {
            const action = await dispatch(allVirtualAccounts({ businessId }));
            if (
                allVirtualAccounts.fulfilled.match(action) &&
                Array.isArray(action.payload)
            ) {
                existingAccount = action.payload.find(
                    (data) =>
                        data.currency === capitalizeCurrencyCode(currencyCode),
                );
            }
        }

        const currency = existingAccount?.currency;
        const shouldUseExistingVA = CURRENCIES_ALLOWED_FOR_VA.includes(currency);
        const shouldRequestVA = !shouldUseExistingVA && !EXCLUDED_CURRENCIES_FOR_VA.includes(currency);
        const shouldFallback = EXCLUDED_CURRENCIES_FOR_VA.includes(currency);

        if (shouldUseExistingVA) {
            handleDataUpdate("accountModal", false);
            handleDataUpdate("showReceiveModal", true);
            return;
        }

        if (shouldRequestVA) {
            await handleRequestVirtualAccount();
            return;
        }

        if (shouldFallback) {
            handleDataUpdate("accountModal", false);
            handleDataUpdate("showReceiveModal", true);
        }
    }, [businessId, currencyCode, dispatch, findVirtualAccount, handleRequestVirtualAccount]);

    const isStableCoin = stableCoinsArray.includes(currencyCode);

    useEffect(() => {
        if (isStableCoin && !enableStableCoins) {
            navigate("/wallets", { replace: true });
        }
    }, [isStableCoin, enableStableCoins, navigate]);

    return (
        <>
            <Loader
                isLoading={
                    walletState?.loading || loading
                }
            />
            <Title>
                <CurrencyFlag currency={CURRENT_WALLET?.currency as string}/>
                {`${capitalizeCurrencyCode(currencyCode)}  balance`}
            </Title>
            <CurrencyBalance>
                <span/>
                {capitalizeCurrencyCode(currencyCode)}
                {formatCurrencyCodeAmount(
                    "",
                    CURRENT_WALLET?.availableBalance?.toFixed(2),
                )}
            </CurrencyBalance>

            <ButtonsDiv>
                {!EXCLUDED_CURRENCIES.includes(currencyCode) &&
                    <Button
                        label="Top up"
                        type="submit"
                        theme="alternate"
                        height="40px"
                        fontSize="12px"
                        onClick={handleTopUp}
                    />
                }

                {EXCLUDED_CURRENCIES.includes(currencyCode) && (
                    <Button
                        theme="secondary"
                        height="40px"
                        label="Top up"
                        fontSize="12px"
                        onClick={() => handleDataUpdate("accountModal", true)}
                        disabled={walletState.accountLoading}
                    />
                )}

                {!CURRENCY_CODE_WITHOUT_CLASS.includes(currencyCode) && (
                    <Button
                        label="All requests"
                        fontSize="12px"
                        height="40px"
                        theme="alternate"
                        onClick={handleOpenRequests}
                    />
                )}

                {!EXCLUDED_CURRENCY_FOR_SEND.includes(currencyCode) && !(hideGhsSendButton && currencyCode === 'ghs' && !GHS_SEND_ALLOW_LIST.includes(business?.email)) && (
                    <Button
                        label="Send"
                        type="submit"
                        theme="alternate"
                        height="40px"
                        fontSize="12px"
                        onClick={handleSend}
                    />
                )}
            </ButtonsDiv>

            <Table/>

            <CustomModal
                isModalVisible={walletState.showReceiveModal}
                onClose={handleCloseModal}
                width="480px"
            >
                {isStableCoin && (
                    <ReceiveStableCoin/>
                )}
                
                {(CURRENCIES_ALLOWED_FOR_VA.includes(capitalizeCurrencyCode(currencyCode))) && (
                    <ReceiveFunds
                        onDone={() => handleDataUpdate("showReceiveModal", false)}
                        account={walletState?.virtualAccount}
                        currency={currencyCode}
                        isVisible={walletState.showReceiveModal}
                    />
                )}

                {!CURRENCIES_ALLOWED_FOR_VA.includes(capitalizeCurrencyCode(currencyCode)) &&
                    !walletState.showKlashaAccount && !isStableCoin && (
                        <ReceiveWithNonNGN
                            onDone={handleReceiveFunds}
                            value={walletState.amount}
                            onValueChange={(amount) => handleDataUpdate("amount", amount)}
                        />
                    )}

                {walletState.showKlashaAccount && (
                    <KlashaAccount
                        title={`Transfer ${capitalizeCurrencyCode(currencyCode)} ${walletState?.amount} to Klasha`}
                        narration={formatLongString(walletState?.tableRef?.narration, 15)}
                        narrationDetail={walletState?.tableRef?.narration}
                        callBack={handleUpdatePayment}
                    />
                )}
            </CustomModal>

            {walletState.showAddModal && (
                <CustomModal
                    isModalVisible={walletState.showAddModal}
                    onClose={() => handleDataUpdate("showAddModal", false)}
                    width="450px"
                >
                    <AddFundsHeader>Add funds to your wallet</AddFundsHeader>

                    <SendPaymentWrapper>
                        <CurrencySelector
                            countries={[
                                {
                                    currencyCode: capitalizeCurrencyCode(currencyCode),
                                    flagCode: capitalizeCurrencyCode(currencyCode),
                                },
                            ]}
                            placeholder="Add funds you'd like to send"
                            value={walletState.addFundsAmount}
                            onValueChange={(val) => handleDataUpdate("addFundsAmount", val)}
                            defaultValue={capitalizeCurrencyCode(currencyCode)}
                            selectValue={capitalizeCurrencyCode(currencyCode)}
                            arrow
                            selector
                        />

                        <ErrorWrapper>
                            {walletState.showMaximumError && (
                                <ErrorMessage
                                    message={`Minimum amount: ${capitalizeCurrencyCode(currencyCode)} ${new Intl.NumberFormat().format(
                                        minimumRecipientAmount,
                                    )}`}
                                />
                            )}
                        </ErrorWrapper>
                    </SendPaymentWrapper>
                </CustomModal>
            )}

            {/*Remove this in the future and use a page instead of modal for payout*/}
            {walletState.showSendModal && (
                <Modal
                    style={{
                        position: "fixed",
                        top: 10,
                        right: 0,
                        bottom: 0,
                        left: 0,
                        zIndex: 50,
                        height: "100%",
                        backgroundColor: "#FFFFFF",
                        paddingTop: "50px",
                        overflow: "scroll",
                    }}
                >
                    <Icon onClick={() => handleDataUpdate("showSendModal", false)}/>
                    <SendFunds
                        close={null}
                        setClose={() => handleDataUpdate("showSendModal", false)}
                    />
                </Modal>
            )}

            <CustomModal
                isModalVisible={walletState.requestModal}
                onClose={() => handleDataUpdate("requestModal", false)}
                width="780px"
            >
                <Heading>All deposit requests</Heading>

                <CustomTable
                    columns={DEPOSIT_REQUEST_COLUMNS}
                    extraEmptyChildren={<EmptyBalanceDescription/>}
                    dataSource={DEPOSIT_REQUEST}
                    currentPage={depositRequestData?.pageNumber}
                    totalItemCount={depositRequestData?.totalCount}
                    onPageChange={onPageChange}
                    handleRowClick={handleRowClick}
                />
            </CustomModal>

            <CustomModal
                isModalVisible={walletState.updatePayment}
                onClose={() => handleDataUpdate("updatePayment", false)}
                width="480px"
            >
                <KlashaAccount
                    title={`Transfer ${capitalizeCurrencyCode(currencyCode)} ${
                        walletState?.tableRef?.narrationAmount
                    } to Klasha`}
                    narration={formatLongString(walletState?.tableRef?.narration, 15)}
                    narrationDetail={walletState?.tableRef?.narration}
                    callBack={handleUpdatePayment}
                />
            </CustomModal>

            <CustomModal
                isModalVisible={walletState.accountDetailsModal}
                width="480px"
                onClose={() => handleDataUpdate("accountDetailsModal", false)}
            >
                <BASSAccount
                    accountName={findVirtualAccount?.accountName || "N/A"}
                    accountBIC={findVirtualAccount?.accountBIC || "N/A"}
                    accountIban={findVirtualAccount?.iban || "N/A"}
                    intermediaryBIC={findVirtualAccount?.bankName || "N/A"}
                    intermediaryBank={findVirtualAccount?.intermediateName || "N/A"}
                />
            </CustomModal>

            <CustomModal
                isModalVisible={walletState.accountModal}
                width="480px"
                onClose={() => handleDataUpdate("accountModal", false)}
            >
                <FundingContent
                    accountName={findVirtualAccount?.accountName || "N/A"}
                    accountBIC={findVirtualAccount?.accountBIC || "N/A"}
                    accountIban={findVirtualAccount?.iban || "N/A"}
                    intermediaryBIC={findVirtualAccount?.bankName || "N/A"}
                    intermediaryBank={findVirtualAccount?.intermediaryBic || "N/A"}
                    navigate={() => navigate(`/wallets/${currencyCode}/fund-account`)}
                    handleAccountTransfer={handleBankTransfer}
                />
            </CustomModal>

            <Loader isLoading={walletState.loading || allDepositRequestLoading}/>
        </>
    );
};

export default Wallet;
